import React from 'react';

import { formatDate } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { route, useAutoNavigate, useMutation } from '@betterment/js-runtime';
import { DropdownMenu, IconButton, ListRow, Shimmer, Text } from '@betterment/samba-react';
import { useNotifications } from '@betterment/shared-components';
import { useNavigate } from 'react-router-dom';

import { useCurrentAdvisor } from '../../../hooks/useCurrentAdvisor';
import urlBuilder from '../../../Shared/url-builder';
import { ExecutedAgreementsDocument } from '../graphql/query.executedAgreement.generated';

const LoadingState: React.FC = () => (
  <ListRow css={{ pt: 'space.200' }}>
    <ListRow.LeadingContent>
      <Shimmer css={{ width: '100%', height: 'size.400', borderRadius: 'radii.full', pt: 'space.50' }} />
    </ListRow.LeadingContent>
    <ListRow.TrailingAsset>
      <Shimmer css={{ width: 'size.400', height: 'size.400', borderRadius: 'radii.full' }} />
    </ListRow.TrailingAsset>
  </ListRow>
);

export const ClientAgreementsCardList = () => {
  const autoNavigate = useAutoNavigate();
  const navigate = useNavigate();
  const { currentAdvisor } = useCurrentAdvisor();
  const isAdmin = currentAdvisor?.isAdmin ?? false;
  const { showNotification } = useNotifications();
  const { data, loading } = useGraphQLQuery(ExecutedAgreementsDocument, { variables: { active: true } });
  const [requestReplaceIntent] = useMutation(route('/', ['agreements', 'client_execute_agreement_intents'])(), {
    method: 'POST',
  });
  const handleView = (id: string) => {
    void autoNavigate(urlBuilder.getDocusignExecutedAgreementDownload(id));
  };
  const handleReplaceIntent = async () => {
    await requestReplaceIntent();
    showNotification({
      type: 'success',
      message: 'Your request to execute a client agreement has been submitted.',
    });
  };

  const agreements = data?.firm.executedAgreements ?? [];

  if (loading) {
    return <LoadingState />;
  }

  return agreements.map(agreement => (
    <ListRow key={agreement.id} css={{ pt: 'space.200' }}>
      <ListRow.LeadingContent>
        <Text>{agreement.name}</Text>
        <Text color="text.weak" variant="text50">
          {agreement.approvedAt && <>Uploaded {formatDate(agreement.approvedAt, 'longOrdinalSlashedNonPadded')} • </>}
          {agreement.isDocusignAgreement ? 'Docusign' : 'Checkbox'}
        </Text>
      </ListRow.LeadingContent>
      <ListRow.TrailingAsset>
        <DropdownMenu>
          <DropdownMenu.Trigger asChild={true}>
            <IconButton label="Show options" name="Ellipses" data-testid="dropdown-client-agreement-card-menu" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="center" side="bottom">
            <DropdownMenu.Item onSelect={() => handleView(agreement.id)}>View</DropdownMenu.Item>
            {isAdmin && (
              <DropdownMenu.Item
                onSelect={() => {
                  void navigate(urlBuilder.getReplaceClientAgreementPage.absolutePath);
                }}
                data-testid="replace-agreement-menu-item"
              >
                Replace
              </DropdownMenu.Item>
            )}
            {isAdmin && (
              <DropdownMenu.Item onSelect={() => void handleReplaceIntent()} data-testid="replace-intent-menu-item">
                Replace intent
              </DropdownMenu.Item>
            )}
          </DropdownMenu.Content>
        </DropdownMenu>
      </ListRow.TrailingAsset>
    </ListRow>
  ));
};
